import { defineStore, getActivePinia } from 'pinia';
import { computed } from 'vue';

import { shellActions } from './shellActions.js';

// The language the app speaks, and the languages it can.
//
// The languages on offer are the ones the application's own vue-i18n holds
// messages for: the application decides what it is translated into, and this
// offers exactly that. What changing the language DOES differs by platform, and
// is in the shell's actions.

const DEFAULT_LANGUAGE = 'en-CA';

// A language is shown in its own name, so a person can find theirs while the
// screen is in one they cannot read. These are therefore not translated.
const OWN_NAMES = {
    'en-CA': 'English',
    'fr-CA': 'Français (canadien)'
};

function ownNameOf(language) {
    if (OWN_NAMES[language]) {
        return OWN_NAMES[language];
    }
    try {
        return new Intl.DisplayNames([language], { type: 'language' }).of(language);
    } catch {
        return language;
    }
}

const useLanguageStore = defineStore('shellLanguage', function () {
    const actions = shellActions().language;

    // The application, reached through the Pinia it installed — the same way
    // the shell's words reach its vue-i18n.
    const application = getActivePinia()?._a ?? null;
    const i18n = application?.config.globalProperties.$i18n ?? null;

    const language = computed(function () {
        if (i18n) {
            return i18n.locale;
        }
        return DEFAULT_LANGUAGE;
    });

    const codes = computed(function () {
        if (i18n) {
            return [...i18n.availableLocales];
        }
        return [DEFAULT_LANGUAGE];
    });

    const available = computed(() => codes.value.map((code) => ({ code, name: ownNameOf(code) })));

    // What a person choosing a language does. Apply first, save second, for the
    // same reason a theme is. A language the app does not offer is ignored.
    async function choose(next) {
        if (!codes.value.includes(next)) {
            return;
        }
        actions.apply(application, next);
        await actions.save(next);
    }

    // Start from what was saved; with nothing saved, from what the platform
    // says a first launch should speak.
    const ready = actions.load().then(function (saved) {
        let first = saved;
        if (!codes.value.includes(first)) {
            first = actions.initial(codes.value);
        }
        if (codes.value.includes(first)) {
            actions.apply(application, first);
        }
    });

    return { language, available, ready, choose };
});

export { useLanguageStore };
